import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { LineChartPro } from "@mui/x-charts-pro/LineChartPro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellRow, FuelCellSample } from "../models/FuelCellRow";

interface Props {
  row: FuelCellRow;
  samples: FuelCellSample[];
}

export function FuelCellRowDetail({ row, samples }: Props) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const dataset = useMemo(
    () =>
      samples.map(s => ({
        time: new Date(s.dateTime).getTime(),
        level: s.data,
      })),
    [samples],
  );

  if (dataset.length < 2) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          {t("fuelCell.detail.notEnough", "Not enough samples in selected range")}
        </Typography>
      </Box>
    );
  }

  const tickFormatter = (value: number) =>
    new Date(value).toLocaleString(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" gap={2} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">
          {t("fuelCell.detail.title", "Fuel level over time")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {row.name || row.licensePlate}
        </Typography>
      </Stack>
      <LineChartPro
        dataset={dataset}
        xAxis={[
          {
            dataKey: "time",
            scaleType: "linear",
            valueFormatter: (value: number) => tickFormatter(value),
            tickLabelStyle: { fontSize: 11 },
          },
        ]}
        yAxis={[
          {
            id: "levelAxis",
            position: "left",
            min: 0,
            max: 100,
            valueFormatter: (value: number) => `${value} %`,
          },
        ]}
        series={[
          {
            dataKey: "level",
            label: t("fuelCell.detail.level", "Level (%)"),
            color: theme.palette.primary.main,
            showMark: false,
            curve: "linear",
            yAxisId: "levelAxis",
            valueFormatter: (value: number | null) =>
              value != null ? `${value.toFixed(1)} %` : "",
          },
        ]}
        height={280}
        margin={{ top: 50, right: 30, bottom: 50, left: 55 }}
        grid={{ horizontal: true }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
      />
    </Box>
  );
}
